import { yReadFile } from './file-utils';
import { yReportCrash } from './debug';
import { the_client_app, the_client_version } from '../me/client';

export namespace $http {
    const ACCEPT_ZIP = 'application/zip';
    const ACCEPT_JSON = 'application/json';
    const CONTENTTYPE_JSON = 'application/json;charset=utf-8';

    interface UpFormat {
        content_type: string;
        serializer: (params: any) => string | Blob | FormData;
    }

    interface DownFormat<Data> {
        accept: string;
        deserializer: (text: any, request: XMLHttpRequest) => Data;
        fetch_response_processor: (response: Response) => Promise<Data>;
        response_type?: XMLHttpRequestResponseType;
    }

    type ProgressCB = (p: number) => void;

    export interface RequestOptions<Data> {
        up?: UpFormat;
        down?: DownFormat<Data>;
        up_progress?: ProgressCB;
        down_progress?: ProgressCB;
        aux?: any;
        no_global_headers?: boolean;
        headers?: { [key: string]: string };
    }

    interface FileResponse {
        name?: string;
        blob: Blob;
    }

    function serialize_json(params: any): string {
        return JSON.stringify(params);
    }

    function deserialize_json(text: any): any {
        return text ? JSON.parse(text) : undefined;
    }

    function deserialize_blob(blob: Blob, request: XMLHttpRequest) {
        const response: FileResponse = { blob: blob };
        const h = request.getResponseHeader('content-disposition');
        if(h) {
            const index = h.indexOf('filename=');
            if(index >= 0)
                response.name = h.substring(index + 9);
        }

        return response;
    }

    function blob_response_processor(response: Response): Promise<FileResponse> {
        return response.blob().then(blob => {
            const r: FileResponse = { blob: blob };
            const h = response.headers.get('Content-Disposition');
            if(h) {
                const index = h.indexOf('filename=');
                if(index >= 0)
                    r.name = h.substring(index + 9);
            }
            return r;
        });
    }

    function json_response_processor(response: Response): Promise<any> {
        return response.text().then(text => { return text ? JSON.parse(text) : undefined });
    }

    type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

    export const json: UpFormat & DownFormat<any> = {
        content_type: CONTENTTYPE_JSON,
        serializer: serialize_json,

        accept: ACCEPT_JSON,
        deserializer: deserialize_json,
        fetch_response_processor: json_response_processor,
    }

    export const zip: DownFormat<FileResponse> = {
        accept: ACCEPT_ZIP,
        deserializer: deserialize_blob,
        fetch_response_processor: blob_response_processor,
        response_type: 'blob',
    }

    const global_headers: { [name: string]: string } = {};

    export function addGlobalHeader(name: string, value: string) {
        global_headers[name] = value;
    }

    export function removeGlobalHeader(name: string) {
        delete global_headers[name];
    }

    // function doRequestWithFetch<Data>(method: HTTPMethod, url: string, headers: Record<string, string>, body: string | Blob | FormData | undefined, down: DownFormat<Data>) {
    //     return fetch(url, { method, headers, body }).then(response => {
    //         if(!response.ok) {
    //             return response.json().then(data => {
    //                 throw { status: response.status, data, method, url };
    //             }, e => {
    //                 $log('http', 'error parsing json response', e);
    //                 throw { status: response.status, data: { message: 'Something went wrong, try again later' }, method, url };
    //             });
    //         }
    //         $log('http', 'Content-Type', response.headers.get('Content-Type'));
    //         return down.fetch_response_processor(response).catch(e => {
    //             $log('http', 'error parsing response', e);
    //             throw { status: -2, data: { message: 'Invalid response from server' }, method, url };
    //         });
    //     }, e => {
    //         $log('http', 'error doing request', e);
    //         throw { status: -1, data: { message: 'Error connecting to server' }, method, url };
    //     });
    // }

    function doRequestWithXHR<Data>(method: HTTPMethod, url: string, headers: Record<string, string>, body: string | Blob | FormData | undefined, down: DownFormat<Data>, up_progress?: ProgressCB, down_progress?: ProgressCB) {
        return new Promise<Data>((resolve, reject) => {
            const request = new XMLHttpRequest();
            request.open(method, url);

            if(down.response_type)
                request.responseType = down.response_type;

            up_progress && request.upload.addEventListener('progress', ev => { up_progress(ev.total > 0 ? ev.loaded / ev.total : 1) })
            down_progress && request.addEventListener('progress', ev => { down_progress(ev.total > 0 ? ev.loaded / ev.total : 1) })

            for(const key in headers) {
                request.setRequestHeader(key, headers[key]);
            }

            request.onload = function(this: XMLHttpRequest) {
                if(this.status >= 200 && this.status < 300) {
                    try {
                        resolve(down.deserializer(this.response, this));
                    } catch(e) {
                        yReportCrash('Error parsing response from ' + url, e?.name)
                        reject({ status: -2, data: { message: 'Invalid response from server' }, method: method, url: url });
                    }
                } else {
                    if(down.response_type === 'blob') {
                        yReadFile(this.response, 'string').then(text => {
                            reject({ status: this.status, data: deserialize_json(text), method: method, url: url })
                        }, () => {
                            reject({ status: -3, data: { message: 'Invalid response from server' }, method: method, url: url });
                        });
                    } else {
                        reject({ status: this.status, data: deserialize_json(this.response), method: method, url: url })
                    }
                }
            };

            request.onerror = function(this: XMLHttpRequest) {
                reject({ status: -1, data: { message: 'Error connecting to server' }, method: method, url: url });
            }

            request.send(body);
        });
    }

    function doRequest<Data>(method: HTTPMethod, url: string, data: any, options: RequestOptions<Data>) {
        const headers: Record<string, string> = {};
        if(!options.no_global_headers && url.startsWith('/'))
            Object.assign(headers, global_headers);
        if(options.headers)
            Object.assign(headers, options.headers);
        if(options.aux)
            headers['X-Buola-Aux'] = JSON.stringify(options.aux);
        const down = options.down || json as DownFormat<Data>;
        headers['Accept'] = down.accept;

        let body: string | Blob | FormData | undefined;

        if(data !== undefined) {
            if(data instanceof Blob || data instanceof FormData) {
                if(data instanceof File)
                    headers['X-Buola-Filename'] = encodeURIComponent(data.name);
                body = data;
            } else {
                const up = options.up || json;
                headers['Content-Type'] = up.content_type;
                body = up.serializer(data);
            }
        }

        return doRequestWithXHR(method, url, headers, body, down, options.up_progress, options.down_progress);
    }

    export function GET<Data = void>(url: string, options: RequestOptions<Data> = {}) {
        return doRequest<Data>('GET', url, undefined, options);
    }

    export function POST<Data = void>(url: string, data?: any, options: RequestOptions<Data> = {}) {
        return doRequest<Data>('POST', url, data, options);
    }

    export function PUT<Data = void>(url: string, data?: any, options: RequestOptions<Data> = {}) {
        return doRequest<Data>('PUT', url, data, options);
    }

    export function DELETE<Data = void>(url: string, options: RequestOptions<Data> = {}) {
        return doRequest<Data>('DELETE', url, undefined, options);
    }

    export function BEACON(url: string, data: any) {
        if(navigator.sendBeacon) {
            try {
                navigator.sendBeacon(url, serialize_json(data));
            } catch {
                POST(url, data, {});
            }
        } else {
            POST(url, data, {});
        }
    }

    addGlobalHeader('X-Abundo-Client', the_client_app + '/' + the_client_version);
}
